import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Sparkles, Music, ListMusic, Clock, Trash2, Play, MoreHorizontal, ArrowRight, Loader2, RefreshCw, AlertTriangle, ShieldCheck, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { API, useAppContext } from '@/App';
import { computeSetWarnings, computeQualityScore } from '@/utils/setWarnings';
import ExportModal from '@/components/ExportModal';

/**
 * Sets page = library of saved AI-built sets.
 *
 * A "Set" is a playlist where `is_suggested === true` (i.e. generated by the
 * AI Assistant / DJ Coach). Manual playlists keep living under /playlists.
 * The chat lives only in the right-rail assistant — this page has no input.
 */
export default function SetsPage() {
  const navigate = useNavigate();
  const { refreshCounts } = useAppContext() || {};
  const [sets, setSets] = useState([]);
  const [allTracks, setAllTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [rebuildingId, setRebuildingId] = useState(null);
  const [exportTarget, setExportTarget] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plRes, trRes, mxRes] = await Promise.all([
        axios.get(`${API}/playlists`),
        axios.get(`${API}/tracks`),
        axios.get(`${API}/mixes`).catch(() => ({ data: { mixes: [] } })),
      ]);
      const all = Array.isArray(plRes.data) ? plRes.data : (plRes.data?.playlists || []);
      setSets(all.filter((p) => p.is_suggested));
      const tracksArr = Array.isArray(trRes.data) ? trRes.data : (trRes.data?.tracks || []);
      const mixesArr = Array.isArray(mxRes.data) ? mxRes.data : (mxRes.data?.mixes || []);
      // /api/tracks excludes mixes, so merge both so sets that reference any
      // long-form track resolve correctly in the card preview.
      setAllTracks([...tracksArr, ...mixesArr]);
    } catch (e) {
      // best-effort — empty state will render
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const trackById = (id) => allTracks.find((t) => t.id === id);

  // Dedupe a set's resolved tracks the same way the backend does for the
  // playlist detail endpoint — by track id AND by (title, artist) signature.
  // Without this the card preview can show the same track twice even though
  // the detail page (which calls /playlists/{id} → backend-deduped) shows it
  // only once → "inconsistent with what's actually in the set".
  const resolveSetTracks = (set) => {
    const ids = set?.track_ids || [];
    const seenIds = new Set();
    const seenSigs = new Set();
    const out = [];
    for (const id of ids) {
      if (seenIds.has(id)) continue;
      const t = trackById(id);
      if (!t) continue;
      const sig = `${(t.title || '').trim().toLowerCase()}|${(t.artist || '').trim().toLowerCase()}`;
      if (sig !== '|' && seenSigs.has(sig)) continue;
      seenIds.add(id);
      seenSigs.add(sig);
      out.push(t);
    }
    return out;
  };

  const deleteSet = async (set) => {
    if (!window.confirm(`Delete set "${set.name}"? This cannot be undone.`)) return;
    setDeletingId(set.id);
    try {
      await axios.delete(`${API}/playlists/${set.id}`);
      toast.success(`Deleted ${set.name}`);
      setSets((s) => s.filter((x) => x.id !== set.id));
      refreshCounts?.();
    } catch (e) {
      toast.error('Failed to delete set');
    } finally {
      setDeletingId(null);
    }
  };

  const rebuildSet = async (set) => {
    // Resolve original brief — either from the saved `brief` field on the
    // playlist (post-Phase-2 sets) or by parsing the description for older
    // ones. Then flip the energy_curve to give the user a meaningfully
    // different second take.
    const brief = resolveBrief(set);
    const flipped = flipCurve(brief.energy_curve || 'build');
    const newBrief = { ...brief, energy_curve: flipped, name: `${set.name?.replace(/ · .*$/, '')} · ${labelFor(flipped)}` };

    setRebuildingId(set.id);
    const toastId = toast.loading(`Rebuilding "${set.name}" with ${labelFor(flipped)}…`);
    try {
      const { data } = await axios.post(`${API}/dj/build-set`, newBrief);
      toast.success(`New version saved: ${data?.playlist?.name || newBrief.name}`, { id: toastId });
      await fetchData();
      refreshCounts?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to rebuild set', { id: toastId });
    } finally {
      setRebuildingId(null);
    }
  };

  return (
    <div className="px-4 sm:px-6 pb-6 space-y-6" data-testid="sets-page">
      {/* Hero card */}
      <section className="glass-pane p-6 md:p-7 flex items-center gap-5 overflow-hidden relative">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#22D3EE]/15 blur-3xl pointer-events-none" />
        <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center shrink-0 shadow-[0_8px_24px_rgba(37,99,255,0.35)]">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] tracking-[0.2em] text-[#2563FF]">AI GENERATED SETS</p>
          <h2 className="font-heading text-2xl font-bold text-[#0F172A] tracking-tight mt-0.5">
            Every set the assistant builds, saved & ready to play
          </h2>
          <p className="font-body text-sm text-[#64748B] mt-1 max-w-2xl">
            Every set the assistant generates lands here. Ask the AI Assistant on the right
            to build a new one — energy curve, harmonic flow, target BPM and all.
          </p>
        </div>
        <button
          onClick={() => {
            // Focus the rail composer
            const i = document.querySelector('[data-testid="assistant-input"]');
            i?.focus();
            i?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          className="btn-primary hidden md:inline-flex items-center gap-2"
          data-testid="sets-build-cta"
        >
          <Sparkles className="w-4 h-4" /> Build a new set
        </button>
      </section>

      {/* Sets grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map((i) => (
            <div key={i} className="glass-pane-soft p-6 h-44 animate-pulse" />
          ))}
        </div>
      ) : sets.length === 0 ? (
        <div className="glass-pane p-12 text-center" data-testid="sets-empty">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] flex items-center justify-center mx-auto mb-4">
            <ListMusic className="w-8 h-8 text-[#2563FF]" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-2">No sets yet</h3>
          <p className="font-body text-[#64748B] text-sm max-w-md mx-auto mb-5">
            Tell the AI Assistant what you want — "build me a 90-min peak time techno set" — and it
            will appear here as soon as the assistant saves it.
          </p>
          <Link to="/playlists" className="text-[#2563FF] text-sm font-semibold hover:underline">
            Or browse your playlists →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4" data-testid="sets-grid">
          {sets.map((s, idx) => {
            const tracks = resolveSetTracks(s);
            const trackCount = tracks.length;
            const bpms = tracks.map((t) => t?.analysis?.bpm).filter(Boolean);
            const minBpm = bpms.length ? Math.min(...bpms).toFixed(0) : null;
            const maxBpm = bpms.length ? Math.max(...bpms).toFixed(0) : null;
            const energies = tracks.map((t) => t?.analysis?.energy).filter(Boolean);
            const warnings = computeSetWarnings(tracks);
            const quality = computeQualityScore(warnings);
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                className="glass-pane p-5 flex flex-col group hover:shadow-[0_24px_50px_-22px_rgba(37,99,255,0.22)] hover:border-[#2563FF]/30 transition-all"
                data-testid={`set-card-${s.id}`}
              >
                <div className="flex items-start gap-3 mb-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-[#2563FF]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm sm:text-base font-semibold text-[#0F172A] truncate">{s.name || 'Untitled Set'}</h3>
                    <p className="text-xs text-[#64748B] mt-0.5 truncate">{trackCount} tracks{minBpm && maxBpm ? ` · ${minBpm}–${maxBpm} BPM` : ''}</p>
                  </div>
                  <button
                    onClick={() => setExportTarget(s)}
                    disabled={trackCount === 0}
                    className="w-7 h-7 rounded-md hover:bg-[#EFF6FF] text-[#94A3B8] hover:text-[#2563FF] flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                    data-testid={`set-export-${s.id}`}
                    title="Export as M3U8"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => deleteSet(s)}
                    disabled={deletingId === s.id}
                    className="w-7 h-7 rounded-md hover:bg-red-50 text-[#94A3B8] hover:text-red-500 flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0"
                    data-testid={`set-delete-${s.id}`}
                    title="Delete set"
                  >
                    {deletingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>

                {/* Energy curve sparkline */}
                <div className="h-12 mb-3">
                  <EnergyCurve values={energies} />
                </div>

                {/* Quick stats */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {minBpm && maxBpm && (
                    <span className="px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#2563FF] border border-[#DBEAFE] font-mono text-[10px]">{minBpm}–{maxBpm} BPM</span>
                  )}
                  {tracks[0]?.analysis?.camelot && (
                    <span className="px-2 py-0.5 rounded-full bg-[#ECFEFF] text-[#0891B2] border border-cyan-200 font-mono text-[10px]">{tracks[0].analysis.camelot}</span>
                  )}
                  {warnings.length === 0 && trackCount >= 2 && (
                    <span
                      className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono text-[10px] flex items-center gap-1"
                      title={`CRATELAB AI Assistant: ${quality.score}/100 · ${quality.verdict}`}
                      data-testid={`set-quality-clean-${s.id}`}
                    >
                      <ShieldCheck className="w-2.5 h-2.5" /> {quality.score} · {quality.verdict}
                    </span>
                  )}
                  {warnings.length > 0 && (
                    <span
                      className={`px-2 py-0.5 rounded-full font-mono text-[10px] flex items-center gap-1 border ${
                        quality.tone === 'red'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : quality.tone === 'amber'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : quality.tone === 'green'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}
                      title={`Quality ${quality.score}/100 · ${quality.verdict}\n\n${warnings.slice(0, 3).map((w) => w.message).join('\n')}`}
                      data-testid={`set-quality-warn-${s.id}`}
                    >
                      <AlertTriangle className="w-2.5 h-2.5" /> {quality.score} · {warnings.length} warning{warnings.length === 1 ? '' : 's'}
                    </span>
                  )}
                  {s.created_at && (
                    <span className="px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0] font-mono text-[10px] flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {new Date(s.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>

                {/* Track previews (first 3) */}
                <div className="space-y-1.5 mb-4 flex-1">
                  {tracks.slice(0, 3).map((t, i) => (
                    <div key={t.id} className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-[#94A3B8] w-4">{i + 1}.</span>
                      <p className="font-normal text-[15px] text-[#3B82F6] truncate flex-1">{t.title}</p>
                      <span className="font-mono text-[#64748B] text-[10px]">{t.analysis?.bpm?.toFixed(0)}</span>
                    </div>
                  ))}
                  {tracks.length > 3 && (
                    <p className="text-[10px] font-mono text-[#94A3B8] pl-6">+{tracks.length - 3} more</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-[#E2E8F0]/60">
                  <button
                    onClick={() => navigate(`/playlist/${s.id}`)}
                    className="flex-1 min-w-0 py-2 px-2 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] text-[#2563FF] text-xs font-semibold hover:bg-[#DBEAFE] transition-colors flex items-center justify-center gap-1.5"
                    data-testid={`set-open-${s.id}`}
                  >
                    <Play className="w-3 h-3 shrink-0" /> <span className="truncate">Open set</span>
                  </button>
                  <button
                    onClick={() => rebuildSet(s)}
                    disabled={rebuildingId === s.id}
                    className="px-3 py-2 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#2563FF] hover:bg-[#EFF6FF] text-[#475569] hover:text-[#2563FF] text-xs font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    data-testid={`set-rebuild-${s.id}`}
                    title={`A/B test: re-run this brief with ${labelFor(flipCurve(resolveBrief(s).energy_curve || 'build'))}`}
                  >
                    {rebuildingId === s.id
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <RefreshCw className="w-3 h-3" />}
                    <span className="hidden sm:inline">Rebuild</span>
                  </button>
                  <button
                    onClick={() => navigate(`/playlist/${s.id}`)}
                    className="w-9 h-9 rounded-xl border border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#64748B] hover:text-[#2563FF] flex items-center justify-center shrink-0"
                    title="More"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {exportTarget && (
        <ExportModal
          kind="playlist"
          id={exportTarget.id}
          name={exportTarget.name}
          onClose={() => setExportTarget(null)}
        />
      )}
    </div>
  );
}

/* ───────────── Brief helpers (Rebuild this set A/B chip) ─────────────
 * AI-generated sets carry their original brief in `playlist.brief` for any
 * set built after the brief-persistence rollout. Older sets store the brief
 * inline in the description string ("BPM 120–130. Genres: techno. Curve:
 * build. Vibe: dark hypnotic"). We parse those as a fallback so existing
 * sets remain rebuildable without a migration.
 */
const CURVE_LABELS = {
  build: 'gentle build',
  warmup: 'warmup',
  hard_peak_then_cooldown: 'peak → cooldown',
  peaks_and_valleys: 'peaks & valleys',
};

function labelFor(curve) {
  return CURVE_LABELS[curve] || curve || 'build';
}

function flipCurve(curve) {
  // Pick the most contrasting alternative — the whole point of the chip is
  // to give the DJ a meaningfully different second take.
  const map = {
    build: 'hard_peak_then_cooldown',
    hard_peak_then_cooldown: 'build',
    warmup: 'peaks_and_valleys',
    peaks_and_valleys: 'warmup',
  };
  return map[curve] || 'hard_peak_then_cooldown';
}

function resolveBrief(set) {
  if (set?.brief && typeof set.brief === 'object') {
    return { ...set.brief };
  }
  // Legacy: scrape the description for parameters we can rebuild from.
  const desc = set?.description || '';
  const brief = {};
  const bpmMatch = desc.match(/BPM\s*([\d]+)\s*[–\-]\s*([\d]+)/i);
  if (bpmMatch) {
    brief.bpm_min = parseInt(bpmMatch[1], 10);
    brief.bpm_max = parseInt(bpmMatch[2], 10);
  }
  const genreMatch = desc.match(/Genres?:\s*([^.]+)/i);
  if (genreMatch) {
    brief.target_genres = genreMatch[1].split(',').map((g) => g.trim()).filter(Boolean);
  }
  const curveMatch = desc.match(/Curve:\s*([a-z_]+)/i);
  if (curveMatch) brief.energy_curve = curveMatch[1];
  const vibeMatch = desc.match(/Vibe:\s*([^.]+)/i);
  if (vibeMatch) brief.vibe = vibeMatch[1].trim();
  return brief;
}

function EnergyCurve({ values = [] }) {
  if (!values.length) {
    // Mock baseline curve when no data
    return (
      <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="ec-empty" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563FF" stopOpacity={0.18} />
            <stop offset="100%" stopColor="#14E6C9" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d="M0 22 Q 25 18 50 14 T 100 18 L 100 30 L 0 30 Z" fill="url(#ec-empty)" />
        <path d="M0 22 Q 25 18 50 14 T 100 18" fill="none" stroke="#94A3B8" strokeWidth="1.2" strokeDasharray="2 2" />
      </svg>
    );
  }
  const w = 100, h = 30, pad = 2;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = Math.max(max - min, 0.1);
  const stepX = (w - pad * 2) / Math.max(values.length - 1, 1);
  const points = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (h - pad * 2) * (1 - (v - min) / range);
    return [x, y];
  });
  const d = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-full">
      <defs>
        <linearGradient id="ec-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2563FF" stopOpacity={0.28} />
          <stop offset="100%" stopColor="#14E6C9" stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={`${d} L ${w - pad} ${h} L ${pad} ${h} Z`} fill="url(#ec-grad)" />
      <path d={d} fill="none" stroke="#2563FF" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
